<template>
	<div
		class="document-tile h-100 w-100"
		:class="{ 'document-tile--selecting': selecting }"
		data-test-id="timeline-document-tile"
		:data-document-kind="isPdf ? 'pdf' : 'file'"
		:aria-label="`Open ${documentName}`">
		<v-tooltip location="bottom" :open-on-focus="false" max-width="280">
			<template #activator="{ props: tooltipProps }">
				<div v-bind="tooltipProps" class="document-tile__header">
					<span class="document-tile__name text-truncate d-block" data-test-id="timeline-document-tile-name">{{
						documentName
					}}</span>
				</div>
			</template>
			<span class="document-tile__tooltip-text">{{ documentName }}</span>
		</v-tooltip>

		<!-- LSP-16659: static document artwork, never a rendered thumbnail of the file (ticket constraint 3).
		     A page card with a folded corner, built from the registered MDI glyphs plus CSS. -->
		<div class="document-tile__body d-flex align-center justify-center" aria-hidden="true">
			<div class="document-tile__page">
				<div class="document-tile__fold"></div>
				<div class="document-tile__lines">
					<span class="document-tile__line" style="width: 70%"></span>
					<span class="document-tile__line" style="width: 88%"></span>
					<span class="document-tile__line" style="width: 58%"></span>
					<span class="document-tile__line" style="width: 80%"></span>
				</div>
				<div v-if="isPdf" class="document-tile__badge document-tile__badge--pdf d-flex align-center justify-center ga-1">
					<v-icon :icon="pdfIcon" size="14" />
					<span>PDF</span>
				</div>
				<div v-else class="document-tile__badge document-tile__badge--file d-flex align-center justify-center ga-1">
					<v-icon icon="mdi-file-document-outline" size="14" />
					<span v-if="fileExtensionLabel">{{ fileExtensionLabel }}</span>
				</div>
			</div>
		</div>

		<slot name="session-label" />
	</div>
</template>

<script setup lang="ts">
	withDefaults(
		defineProps<{
			documentName: string;
			isPdf: boolean;
			fileExtensionLabel?: string;
			pdfIcon?: string;
			selecting?: boolean;
		}>(),
		{ fileExtensionLabel: "", selecting: false, pdfIcon: "mdi-file-pdf-box" }
	);
</script>

<style scoped lang="scss">
	.document-tile {
		position: relative;
		overflow: hidden;
		border-radius: 4px;
		background-color: #eef1f6;
		transition: background-color 0.2s ease;

		&:hover {
			background-color: #e6eaf2;
		}
	}

	.document-tile__header {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		z-index: 2;
		padding: 6px 8px;
		background-color: rgba(255, 255, 255, 0.96);
		border-bottom: 1px solid #e3e6ec;
	}

	// The selection bubble sits top-right in select mode; keep the name from running under it.
	.document-tile--selecting .document-tile__header {
		padding-right: 34px;
	}

	.document-tile__name {
		font-size: 0.75rem;
		font-weight: 600;
		color: rgb(var(--v-theme-on-surface));
	}

	.document-tile__tooltip-text {
		word-break: break-word;
	}

	.document-tile__body {
		position: absolute;
		inset: 0;
		padding-top: 28px;
	}

	.document-tile__page {
		position: relative;
		width: 52%;
		aspect-ratio: 11 / 13;
		max-height: 72%;
		background-color: #fff;
		border: 1px solid #dde2eb;
		border-radius: 3px;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
		// Clip the top-right corner so the fold reads as a turned-down page.
		clip-path: polygon(0 0, 78% 0, 100% 18%, 100% 100%, 0 100%);
	}

	.document-tile__fold {
		position: absolute;
		top: 0;
		right: 0;
		width: 22%;
		height: 18%;
		background: linear-gradient(225deg, transparent 50%, #eaeef5 50%);
	}

	.document-tile__lines {
		position: absolute;
		top: 26%;
		left: 12%;
		right: 12%;
		display: flex;
		flex-direction: column;
		gap: 5%;
		height: 30%;
	}

	.document-tile__line {
		display: block;
		height: 3px;
		border-radius: 2px;
		background-color: #d8dde6;
	}

	.document-tile__badge {
		position: absolute;
		left: 12%;
		bottom: 16%;
		padding: 2px 6px;
		border-radius: 4px;
		font-size: 0.7rem;
		font-weight: 800;
		letter-spacing: 0.04em;
		line-height: 1.4;
	}

	.document-tile__badge--pdf {
		background-color: #e53935;
		color: #fff;
	}

	.document-tile__badge--file {
		background-color: #e6e9ef;
		color: rgba(var(--v-theme-on-surface), 0.7);
	}
</style>
